import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Alert,
  Grid,
  TextField,
  Tooltip,
  InputAdornment,
  IconButton,
  Typography,
  Autocomplete,
  FormControl,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { Add } from '@mui/icons-material';
// import { useSnackbar } from '../../stateless/SnackBarProvider';
import SearchIcon from '@mui/icons-material/Search';
import { GridRowId, GridColDef } from '@mui/x-data-grid';
// import CommonDataGrid from '../../Stateless/CommonDataGrid';
// import ErrorHandler from '../../Stateless/Common/ErrorHandler';
// import Loading from '../../Stateless/Common/Loading';
import {COLORS} from "../../../theme/themeConstants"
// import ActionButtons from '../../Stateless/Common/ActionButtons';
import { atom, useSetAtom } from 'jotai';
//import DialogContent from '@mui/material/DialogContent';
import AddStoreItem from './AddStoreItem';
import Button from '@mui/material/Button';
// import {
//   useBlockUnblockStoreItemById,
//   useDeleteStoreItemById,
//   useGetAllStoreItem,
// } from '../../Query/StoreItemQuery';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import { AddOrEditItemRequestSchemaItemAvailableTimeEnum } from '../../../client-sdk';
import { categoryStyles } from '../../Styles/CategoryFormStyles';
import { useSnackbar } from '../../stateless/SnackBarProvider';
import { useBlockUnblockStoreItemById, useDeleteStoreItemById, useGetAllStoreItem } from '../../Query/StoreItemQuery';
import BulkUploadModal from './BulkUploadModal';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ActionButtons from '../../stateless/Common/ActionButtons';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import ErrorHandler from '../../stateless/Common/ErrorHandler';
import Loading from '../../stateless/Common/Loading';
import { useItemManagementParams } from './useItemManagementParams';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useDebounce } from '../../../utils/useDounce';
import { useTranslation } from 'react-i18next';

interface StoreItem {
  id: string;
  itemName: string;
  itemNameArabic: string;
  itemCode: string;
  restaurantName: string;
  categoryName?: string;
  subCategoryName?: string;
  itemCategoryId: string;
  itemSubCategoryId: string;
  restaurantId: string;
  originalPrice: number;
  discountedPrice: number;
  itemQuantity: number;
  itemContent: string;
  itemContentArabic: string;
  discount: boolean;
  vegOrNonVeg: 'veg' | 'non-veg';
  tax: boolean;
  taxName: string;
  taxNameArabic: string;
  taxPercentage: number;
  comboPack: boolean;
  isHalal: boolean;
  itemAvailableTime: AddOrEditItemRequestSchemaItemAvailableTimeEnum[];
  description: string;
  descriptionArabic: string;
  metaKeywords: string;
  metaKeyWordsArabic: string;
  metaDescription: string;
  metaDescriptionArabic: string;
  hasChoice: boolean;
  hasSpecification: boolean;
  itemSpecification: {
    specificationTitle: string;
    specificationDescription: string;
  }[];
  itemImage: string[];
  isStockAvailable: string;
  addedBy: 'Admin' | 'Merchant';
  status: 'blocked' | 'unblocked';
}

interface EditFormData extends Omit<StoreItem, 'isStockAvailable'> {
  isStockAvailable: boolean;
}

type StoreItemListProps = {
  editCategory: (category: StoreItem) => void;
  modal?: boolean;
  storeId?: string;
};
export const categoryStoreAtom = atom<string | null>(null);

const StoreItemList: React.FC<StoreItemListProps> = ({ editCategory}) => {
  const { showMessage } = useSnackbar();
  const { t, i18n } = useTranslation();
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const setCategoryStoreAtom = useSetAtom(categoryStoreAtom);
  const [editMode, setEditMode] = useState<'add' | 'edit'>('add');
  const [selectedItem, setSelectedItem] = useState<EditFormData | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage] = useState<string>('');
  const [snackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>(
    'info'
  );
  const [, forceUpdate] = useState({});

  type StoreOption = { label: string; value: string };

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  // Store filter dropdown options
  const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);

  // Item management params (includes search, pagination, and storeId filter)
  const { params, dispatch } = useItemManagementParams();
  //  const memoizedParams = useMemo(() => ({ ...params, merchantId: params.merchantId }), [params]);
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  // Store items query (supports storeId filter via params)
  const { data: itemManagement, isLoading, refetch } = useGetAllStoreItem(memoizedParams);

  // Fetch all stores for the current merchant to populate the store filter dropdown
  const storeQueryParams = useMemo(
    () => ({
      sortBy: 'createdAt',
      sortOrder: 'asc',
      page: 1,
      limit: 100,
      addedByFilter: 'All',
      statusFilter: 'All',
      search: undefined,
      storeType: undefined,
    }),
    []
  );

  const { data: storesData } = useAllStores(storeQueryParams);

  useEffect(() => {
    if (storesData && Array.isArray(storesData.data)) {
      const options: StoreOption[] = storesData.data.map((store: any) => ({
        value: store.id,
        label: store.nameEnglish || store.restaurantDetails?.storeName || '',
      }));
      setStoreOptions(options);
    } else {
      setStoreOptions([]);
    }
  }, [storesData]);

  const deleteStoreItemMutation = useDeleteStoreItemById();
  const { mutateAsync: blockUnblockStoreItemAsync } =
    useBlockUnblockStoreItemById();

  const handleOpenAddModal = () => {
    setEditMode('add');
    setSelectedItem(null);
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setEditMode('add');
    setSelectedItem(null);
  };

  const handleEditCategory = (category: StoreItem) => {
    // Convert isStockAvailable from string to boolean for EditFormData
    const editData: EditFormData = {
      ...category,
      isStockAvailable: category.isStockAvailable === 'Yes',
    };
    setSelectedItem(editData);
    setEditMode('edit');
    setAddModalOpen(true);
    editCategory(category); // Call the editCategory prop
  };

  const filteredRows = React.useMemo(() => {
    if (!itemManagement?.data) return [];
    const searchLower = params.search?.toLowerCase().trim() || '';
    const storeOwners = itemManagement?.data || []

    const filtered = storeOwners
      .filter((row) => {

        if (!row) return false;
        // Apply store-based filter if a storeId is selected
        if (params.storeId && row.storeId && row.storeId !== params.storeId) {
          return false;
        }
        const name = (row.itemName || '').toLowerCase();
        return (
          name.includes(searchLower)
        );
      })


    return filtered.map((row, index) => ({
      ...row,
      serialNumber: index + 1 + (Number(params.page - 1)) * Number(params.limit),
      id: row.itemId || '',
      itemName: i18n.language === 'ar' ? row.itemNameArabic || row.itemName || '' : row.itemName || '',
      itemNameArabic: row.itemNameArabic || '',
      itemCode: row.itemCode || '',
      restaurantName: row.storeName || '',
      restaurantId: row.storeId || '',
      itemCategoryId: row.itemCategoryId || '',
      itemSubCategoryId: row.itemSubCategoryId || '',
      originalPrice: row.originalPrice || 0,
      discountedPrice: row.discountedPrice != null ? row.discountedPrice : null,
      itemQuantity: row.itemQuantity || 0,
      itemContent: row.itemContent || '',
      itemContentArabic: row.itemContentArabic || '',
      discount: row.discount || false,
      vegOrNonVeg: row.vegOrNonVeg || 'non-veg',
      tax: row.tax || false,
      taxName: row.taxName || '',
      taxNameArabic: row.taxNameArabic || '',
      taxPercentage: row.taxPercentage || 0,
      comboPack: row.comboPack || false,
      isHalal: row.isHalal || false,
      itemAvailableTime: row.itemAvailableTime || [],
      description: row.description || '',
      descriptionArabic: row.descriptionArabic || '',
      metaKeywords: row.metaKeywords || '',
      metaKeyWordsArabic: row.metaKeyWordsArabic || '',
      metaDescription: row.metaDescription || '',
      metaDescriptionArabic: row.metaDescriptionArabic || '',
      hasChoice: row.hasChoice || false,
      hasSpecification: row.hasSpecification || false,
      itemSpecification: (row.itemSpecification || []).map((spec) => ({
        specificationTitle: spec.specificationTitle || '',
        specificationDescription: spec.specificationDescription || '',
      })),
      itemImage: row.itemImage || [],
      isStockAvailable: row.isStockAvailable ? t('storeItem.yes') : t('storeItem.no'),
      addedBy: row.addedBy || 'Admin',
      status: row.status || 'unblocked',
    }));
  }, [params.search, itemManagement, params.page, params.limit, i18n.language, t]);



  const handleBlock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Store item selected to block', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to block ${selectedCategoryIds.length} Store item(s)?`
      )
    ) {
      try {
        await blockUnblockStoreItemAsync({
          id: selectedCategoryIds,
          data: { status: 'blocked' },
        });

        refetch();
        setSelectedCategoryIds([]);
        showMessage('Store item blocked successfully', 'success');
      } catch {
        showMessage('Failed to block StoreItem', 'error');
      }
    }
  };

  const handleUnblock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Store Item selected to unblock', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to unblock ${selectedCategoryIds.length} Store item(s)?`
      )
    ) {
      try {
        await blockUnblockStoreItemAsync({
          id: selectedCategoryIds,
          data: { status: 'unblocked' },
        });

        refetch()
        showMessage('Store Item unblocked successfully', 'success');
        setSelectedCategoryIds([]);
      } catch {
        showMessage('Failed to unblock Store Item', 'error');
      }
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this Store Item?')) {
      deleteStoreItemMutation.mutate([id], {
        onSuccess: () => {
          refetch()
          setSelectedCategoryIds([]);
        },
      });
    }
  };

  const handleDeleteSelected = () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Store Items selected to delete', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedCategoryIds.length} Store Item(s)?`
      )
    ) {
      deleteStoreItemMutation.mutate(selectedCategoryIds, {
        onSuccess: () => {
          showMessage(
            `${selectedCategoryIds.length} Store Item(s) deleted successfully`,
            'success'
          );
          setSelectedCategoryIds([]);
        },
        onError: (error: any) => {
          showMessage(error?.message || 'Failed to delete Store Item', 'error');
        },
      });
    }
  };


  const columns = useMemo(() => [
    { field: 'serialNumber', headerName: t('storeItem.columns.sno'), width: 90 },
    { field: 'itemName', headerName: t('storeItem.columns.itemName'), flex: 1 },
    { field: 'itemCode', headerName: t('storeItem.columns.itemCode'), flex: 1 },
    { field: 'restaurantName', headerName: t('storeItem.columns.restaurantName'), flex: 1 },
    { field: 'originalPrice', headerName: t('storeItem.columns.originalPrice'), flex: 1 },
    {
      field: 'discountedPrice',
      headerName: t('storeItem.columns.discountedPrice'),
      flex: 1,
      renderCell: (params: { value: number | null | undefined; row: { discount?: boolean } }) => {
        const value = params.value;
        const hasDiscount = params.row.discount;
        // Show empty if discount is not enabled or if discountedPrice is not set
        if (!hasDiscount || value == null || value === 0) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        return <Typography variant="body2">{value}</Typography>;
      },
    } as GridColDef,
    {
      field: 'itemImage',
      headerName: t('storeItem.columns.itemImage'),
      flex: 1,
      renderCell: (params: { row: { itemImage: any[] } }) => {
        const imageUrl = params.row.itemImage?.[0]; // Get the first image URL
        return imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt="Item"
            sx={{
              width: 50,
              height: 50,
              objectFit: 'cover',
              borderRadius: 1,
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('storeItem.noImage')}
          </Typography>
        );
      },
    },
    { field: 'isStockAvailable', headerName: t('storeItem.columns.stockAvailability'), flex: 1 },
    { field: 'addedBy', headerName: t('storeItem.columns.addedBy'), width: 100 },
    {
      field: 'status',
      headerName: t('storeItem.columns.status'),
      disableColumnMenu: true,
      sortable: false,
      width: 90,
      renderCell: (params: any) => {
        const isBlocked = params.row.status !== 'unblocked';

        const toggleStatus = async () => {
          const newStatus = isBlocked ? 'unblocked' : 'blocked';
          const actionText = isBlocked ? 'unblock' : 'block';

          const confirmed = window.confirm(
            `Are you sure you want to ${actionText} StoreItem?`
          );
          if (!confirmed) return;

          try {
            await blockUnblockStoreItemAsync({
              id: [params.row.id],
              data: { status: newStatus },
            });

            refetch()
            showMessage(
              `StoreItem ${newStatus === 'unblocked' ? 'unblocked' : 'blocked'} successfully`,
              'success'
            );
          } catch (error) {
            console.error(error);
            showMessage('Failed to update StoreItem status', 'error');
          }
        };

        return (
          <Tooltip title={isBlocked ? 'Block' : 'Unblock'}>
            <IconButton size="small">
              {isBlocked ? (
                <BlockIcon
                  color="error"
                  fontSize="small"
                  onClick={toggleStatus}
                />
              ) : (
                <CheckCircleIcon
                  color="success"
                  fontSize="small"
                  onClick={toggleStatus}
                />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: t('storeItem.columns.actions'),
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);
        return (
          <Box>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem onClick={() => { setAnchorEl(null); handleEditCategory(params.row); setCategoryStoreAtom(params.row.id); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('storeItem.editAction')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); handleDelete(params.id); }}>
                <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>{t('storeItem.deleteAction')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        );
      },
    },
  ], [t, handleEditCategory, handleDelete, setCategoryStoreAtom]);

  const handleCategorySelection = (selectionModel: GridRowId[]) => {
    const ids = selectionModel.map((id) => String(id));
    setSelectedCategoryIds(ids);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  const selectedStoreOption = useMemo(
    () => storeOptions.find((opt) => opt.value === params.storeId) || null,
    [storeOptions, params.storeId]
  );

  const handleStoreFilterChange = (_: any, newValue: StoreOption | null) => {
    dispatch({ type: 'SET_STORE_ID', payload: newValue ? newValue.value : undefined });
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  // const handleFilterChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
  //   dispatch({ type: 'SET_SORT', payload: { sortBy, sortOrder } });
  // };

  return (
    <Box sx={categoryStyles.ListContainer}>
      {snackbarOpen && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity={snackbarSeverity}
            onClose={() => setSnackbarOpen(false)}
          >
            {snackbarMessage}
          </Alert>
        </Box>
      )}

      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        wrap="wrap"
        sx={{ mb: 3 }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
          <Box
            sx={{
              width: { xs: '100%', sm: 280 },
              maxWidth: '100%',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              background: '#fff',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#bdbdbd',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
              },
              '&:focus-within': {
                borderColor: 'primary.main',
                boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)',
              },
            }}
          >
            <FormControl
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  height: 48,
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Autocomplete
                options={storeOptions}
                getOptionLabel={(option) => option.label || ''}
                value={selectedStoreOption}
                onChange={handleStoreFilterChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('storeItem.form.placeholders.selectStore')}
                    variant="outlined"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 48,
                        border: 'none',
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& .MuiInputBase-input': {
                        paddingY: 1.5,
                        paddingX: 1.5,
                        fontSize: '14px',
                        color: 'text.primary',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        fontSize: '14px',
                        color: 'text.secondary',
                        opacity: 0.7,
                      },
                    }}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    paddingX: '14px !important',
                  },
                }}
              />
            </FormControl>
          </Box>

          <Box
            sx={{
              width: { xs: '100%', sm: 400, md: 500 },
              maxWidth: '100%',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              background: '#fff',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#bdbdbd',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
              },
              '&:focus-within': {
                borderColor: 'primary.main',
                boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)',
              },
            }}
          >
            <TextField
              fullWidth
              size="medium"
              placeholder={t('storeItem.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 48,
                  border: 'none',
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  paddingY: 1.5,
                  paddingX: 0.5,
                  fontSize: '14px',
                  color: 'text.primary',
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '14px',
                  color: 'text.secondary',
                  opacity: 0.7,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 1.5 }}>
                    <SearchIcon 
                      sx={{ 
                        fontSize: 20, 
                        color: 'text.secondary',
                        opacity: 0.7,
                      }} 
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
        <Grid
          container
          justifyContent="flex-end"
          spacing={1}
          alignItems="center"
        >
          <Grid>
            <ActionButtons
              onBlock={handleBlock}
              onUnblock={handleUnblock}
              onDelete={handleDeleteSelected}
              showBlock={selectedCategoryIds.length > 0 && filteredRows.some(r => selectedCategoryIds.includes(r.id) && r.status === 'unblocked')}
              showUnblock={selectedCategoryIds.length > 0 && filteredRows.some(r => selectedCategoryIds.includes(r.id) && r.status === 'blocked')}
              showDelete={selectedCategoryIds.length > 0}
            />
          </Grid>
          <Grid>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setBulkUploadModalOpen(true)}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: 'none',
                mr: 1,
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
                color: COLORS.secondary.main,
                borderColor:COLORS.secondary.main
              }}
              startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
            >
              {t('storeItem.bulkUpload.button')}
            </Button>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleOpenAddModal}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              }}
              startIcon={<Add sx={{ fontSize: 16}} />}
            >
              {t('storeItem.add')}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <CommonDataGrid
        key={`datagrid-page-${params.page}-limit-${params.limit}`}
        rows={filteredRows}
        columns={columns}
        page={params.page - 1}
        pageSize={params.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        checkboxSelection
        selectionModel={selectedCategoryIds}
        onRowSelectionModelChange={handleCategorySelection}
        loading={isLoading}
        rowCount={itemManagement?.pagination?.totalItems || 0}
        // setQuery={(queryParams) => {
        //   dispatch({ type: 'SET_PAGE', payload: queryParams.page });
        //   dispatch({ type: 'SET_LIMIT', payload: queryParams.limit });
        // }}
        components={{
          LoadingOverlay: Loading,
          NoRowsOverlay: () => (
            <ErrorHandler
              type="no-data"
              message={t('storeItem.noData')}
              onRetry={function (): void {
                throw new Error('Function not implemented.');
              }}            />
          ),
        }}
      />

      <EditStoreOwnerModal
        open={addModalOpen}
        onClose={handleCloseAddModal}
        title={editMode === 'add' ? t('storeItem.add') : t('storeItem.edit')}
      >
        <AddStoreItem
          onClose={handleCloseAddModal}
          mode={editMode}
          initialData={selectedItem}
        />
      </EditStoreOwnerModal>

      <EditStoreOwnerModal
        open={bulkUploadModalOpen}
        onClose={() => setBulkUploadModalOpen(false)}
        title={t('storeItem.bulkUpload.title')}
        size="md"
      >
        <BulkUploadModal
          open={bulkUploadModalOpen}
          onClose={() => {
            setBulkUploadModalOpen(false);
            refetch();
          }}
        />
      </EditStoreOwnerModal>
    </Box>
  );
};

export default StoreItemList;
